import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Trash2, Tag, Check, X, Calendar, DollarSign, Percent } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDiscounts = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: '',
    percent: 10,
    min_order_amount: 0,
    expires_at: '',
    active: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('adminToken');
  const authHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadCodes();
  }, []);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/discount-codes`, { headers: authHeaders });
      if (!res.ok) throw new Error('Yüklenemedi');
      const data = await res.json();
      setCodes(data.codes || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        code: form.code.trim().toUpperCase(),
        percent: parseInt(form.percent),
        min_order_amount: parseFloat(form.min_order_amount || 0),
        expires_at: form.expires_at || null,
        active: form.active
      };
      const res = await fetch(`${API_URL}/api/admin/discount-codes`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Oluşturulamadı');
      
      setShowForm(false);
      setForm({ code: '', percent: 10, min_order_amount: 0, expires_at: '', active: true });
      loadCodes();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await fetch(`${API_URL}/api/admin/discount-codes/${code.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ active: !code.active })
      });
      loadCodes();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`"${code.code}" kodunu silmek istediğinize emin misiniz?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/discount-codes/${code.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      loadCodes();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return '-'; }
  };

  const isExpired = (d) => {
    if (!d) return false;
    try { return new Date(d) < new Date(); } catch { return false; }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Tag className="text-red-600" />
            İndirim Kodları
          </h1>
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-discount-code-btn"
            className="bg-red-600 text-white px-5 py-2.5 rounded-lg hover:bg-red-700 flex items-center gap-2 font-semibold shadow-md"
          >
            <Plus size={20} /> Yeni Kod Ekle
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Yeni İndirim Kodu</h2>
                <button onClick={() => { setShowForm(false); setError(null); }} className="text-gray-500 hover:text-gray-800">
                  <X size={24} />
                </button>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4 text-sm">
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kod *</label>
                  <input
                    type="text"
                    required
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="ÖRN: YENI10"
                    data-testid="discount-code-field"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 uppercase"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İndirim Yüzdesi (%) *</label>
                  <input
                    type="number"
                    required min="1" max="100"
                    value={form.percent}
                    onChange={(e) => setForm({ ...form, percent: e.target.value })}
                    data-testid="discount-percent-field"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Sepet Tutarı (TL)</label>
                  <input
                    type="number"
                    min="0" step="0.01"
                    value={form.min_order_amount}
                    onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })}
                    placeholder="0 = Sınırsız"
                    data-testid="discount-min-order-field"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Son Kullanma Tarihi</label>
                  <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    data-testid="discount-expires-field"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız, kod süresiz geçerli olur</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="w-4 h-4 accent-red-600"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">Aktif</label>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setError(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-discount-btn"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
                  >
                    {submitting ? 'Ekleniyor...' : 'Kodu Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">Yükleniyor...</div>
          ) : codes.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Tag size={48} className="mx-auto text-gray-300 mb-3" />
              Henüz indirim kodu eklenmemiş
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Kod</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">İndirim</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Min. Tutar</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Son Tarih</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Kullanım</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Durum</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => {
                    const expired = isExpired(c.expires_at);
                    return (
                      <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`discount-row-${c.code}`}>
                        <td className="py-3 px-4">
                          <span className="font-mono font-bold text-red-600 bg-red-50 px-3 py-1 rounded">
                            {c.code}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-1 text-green-700 font-semibold">
                            <Percent size={14} /> %{c.percent}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-700">
                          {c.min_order_amount > 0 ? `${c.min_order_amount.toLocaleString('tr-TR')} TL` : '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-700">
                          <span className={expired ? 'text-red-500' : ''}>
                            {formatDate(c.expires_at)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-700">
                          {c.used_count || 0}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleToggleActive(c)}
                            data-testid={`toggle-discount-${c.code}`}
                            className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                              c.active && !expired
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {c.active && !expired ? <><Check size={12} /> Aktif</> : <><X size={12} /> {expired ? 'Süresi Doldu' : 'Pasif'}</>}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDelete(c)}
                            data-testid={`delete-discount-${c.code}`}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            aria-label="Sil"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Tag size={18} /> İndirim Kodu Kuralları
          </h3>
          <ul className="text-sm text-blue-700 space-y-1 pl-5 list-disc">
            <li>Her müşteri her kodu yalnızca <strong>1 kere</strong> kullanabilir</li>
            <li>Minimum sepet tutarı <strong>0</strong> ise her tutarda geçerli</li>
            <li>Son kullanma tarihi <strong>boş</strong> ise kod süresiz çalışır</li>
            <li>Pasif kodlar sepette uygulanamaz</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDiscounts;
